/**
 * Typed Patient Notification filter state and API mapping helpers.
 */

/** Inclusive next-appointment date bounds selected by a user. */
export interface AppointmentRangeFilter {
  from?: string;
  to?: string;
}

/** Applied Patient Notification filters controlled by the page. */
export interface NotificationFilters {
  diagnosis?: string[];
  stage?: string[];
  insurance?: string[];
  signoff?: string[];
  patientTags?: string[];
  nextAppointment?: AppointmentRangeFilter;
}

/** Filter dimensions rendered as multi-select checkbox lists. */
export type NotificationMultiSelectKey =
  | "diagnosis"
  | "stage"
  | "insurance"
  | "signoff"
  | "patientTags";

/** Available facet values and patient counts keyed by UI filter dimension. */
export type NotificationFacetOptions = Partial<
  Record<NotificationMultiSelectKey, Record<string, number>>
>;

export const FILTER_LABELS: Record<
  NotificationMultiSelectKey | "nextAppointment",
  string
> = {
  diagnosis: "Diagnosis",
  stage: "Stage",
  insurance: "Insurance",
  signoff: "Signoff Details",
  patientTags: "Patient Tags",
  nextAppointment: "Next Appointment",
};

const FACET_KEYS: Record<NotificationMultiSelectKey, string> = {
  diagnosis: "diagnosis",
  stage: "stage",
  // "Insurance" filters the normalized payer class (Commercial/Medicare/…) shown in the column.
  insurance: "insurance_category",
  signoff: "signoff",
  patientTags: "patient_tags",
};

/**
 * Remove empty values and duplicate selections from applied filters.
 *
 * @param filters - Candidate notification filters
 * @returns Canonical filters suitable for storage and API requests
 */
export function normalizeNotificationFilters(
  filters: NotificationFilters,
): NotificationFilters {
  const normalized: NotificationFilters = {};
  (Object.keys(FACET_KEYS) as NotificationMultiSelectKey[]).forEach((key) => {
    const values = Array.from(
      new Set((filters[key] ?? []).filter((value) => value.trim())),
    );
    if (values.length) normalized[key] = values;
  });
  const from = filters.nextAppointment?.from || undefined;
  const to = filters.nextAppointment?.to || undefined;
  if (from || to) normalized.nextAppointment = { from, to };
  return normalized;
}

/**
 * Build backend facet filters from the controlled UI filter state.
 *
 * @param filters - Applied notification filters
 * @returns Backend facet key to selected values
 */
export function buildNotificationFacetFilters(
  filters: NotificationFilters,
): Record<string, string[]> | undefined {
  const payload: Record<string, string[]> = {};
  (Object.keys(FACET_KEYS) as NotificationMultiSelectKey[]).forEach((key) => {
    const values = filters[key];
    if (values?.length) payload[FACET_KEYS[key]] = values;
  });
  return Object.keys(payload).length ? payload : undefined;
}

/**
 * Map backend facet names into the typed view model.
 *
 * @param facets - Backend facet counts keyed with snake_case names
 * @returns Filter options keyed for the React controls
 */
export function mapNotificationFacetOptions(
  facets: Record<string, Record<string, number>>,
): NotificationFacetOptions {
  return {
    diagnosis: facets.diagnosis ?? {},
    stage: facets.stage ?? {},
    insurance: facets.insurance_category ?? {},
    signoff: facets.signoff ?? {},
    patientTags: facets.patient_tags ?? {},
  };
}

/**
 * Produce a stable cache suffix for one applied filter combination.
 *
 * @param filters - Applied notification filters
 * @returns Deterministically ordered JSON cache key
 */
export function notificationFiltersKey(filters: NotificationFilters): string {
  const normalized = normalizeNotificationFilters(filters);
  const ordered = Object.fromEntries(
    Object.entries(normalized)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? [...value].sort() : value,
      ]),
  );
  return JSON.stringify(ordered);
}

/**
 * Count active filter badges, treating one appointment range as one filter.
 *
 * @param filters - Applied notification filters
 * @returns Number of individually removable badges
 */
export function countNotificationFilters(filters: NotificationFilters): number {
  const normalized = normalizeNotificationFilters(filters);
  // Sum selected values across every multi-select facet (so new categories like insurance /
  // signoff are counted automatically) plus the appointment range as one.
  const facetCount = (
    Object.keys(FACET_KEYS) as NotificationMultiSelectKey[]
  ).reduce((total, key) => total + (normalized[key]?.length ?? 0), 0);
  return facetCount + (normalized.nextAppointment ? 1 : 0);
}
